import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ArrowUpRight, Loader2, Check } from "lucide-react";
import { Testimonials } from "../components/sections/Testimonials";
import { VoiceRecorder } from "../components/sections/VoiceRecorder";
import { COMPANY, SERVICES, BUDGET_OPTIONS } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent. We'll be in touch within 1 business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or call us directly.";
      toast.error(typeof detail === "string" ? detail : "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      {/* HEADER */}
      <section className="relative pt-20 pb-16 md:pt-28 md:pb-20 overflow-hidden border-b border-zinc-800/70">
        <div className="rm-glow-top-right" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="rm-overline mb-5">Free first call</div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter text-white max-w-4xl leading-[0.95]">
            Let's talk about getting you{" "}
            <span className="text-violet-400">more leads</span>.
          </h1>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl leading-relaxed">
            Tell us a bit about your business and where you'd like to go.
            We'll reply within one business day — no slide decks, no pressure.
          </p>
        </div>
      </section>

      {/* FORM + INFO */}
      <section
        data-testid="contact-main"
        className="relative py-20 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Info column */}
          <div className="lg:col-span-5">
            <div className="rm-overline mb-4">Direct channels</div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-white leading-tight">
              Prefer to skip the form?
            </h2>
            <p className="mt-4 text-zinc-400">
              Call or email and you'll reach the same person who'd actually be
              running your ads.
            </p>

            <div className="mt-8 space-y-3">
              <a
                href={COMPANY.phoneHref}
                data-testid="contact-phone-card"
                className="rm-card p-6 flex items-start gap-4 group"
              >
                <div className="h-11 w-11 flex items-center justify-center border border-zinc-800 text-violet-400 bg-zinc-900/60 flex-shrink-0">
                  <Phone className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="rm-overline mb-1">Phone</div>
                  <div className="font-display text-xl font-bold text-white group-hover:text-violet-300 transition-colors">
                    {COMPANY.phoneDisplay}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 font-mono">
                    Mon–Fri · 9:00–17:00 PT
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-zinc-500 group-hover:text-violet-400 transition-colors" />
              </a>

              <a
                href={COMPANY.emailHref}
                data-testid="contact-email-card"
                className="rm-card p-6 flex items-start gap-4 group"
              >
                <div className="h-11 w-11 flex items-center justify-center border border-zinc-800 text-violet-400 bg-zinc-900/60 flex-shrink-0">
                  <Mail className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="rm-overline mb-1">Email</div>
                  <div className="font-display text-xl font-bold text-white group-hover:text-violet-300 transition-colors break-all">
                    {COMPANY.email}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 font-mono">
                    Reply within 1 business day
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-zinc-500 group-hover:text-violet-400 transition-colors" />
              </a>

              <div
                data-testid="contact-location-card"
                className="rm-card p-6 flex items-start gap-4"
              >
                <div className="h-11 w-11 flex items-center justify-center border border-zinc-800 text-violet-400 bg-zinc-900/60 flex-shrink-0">
                  <MapPin className="h-4 w-4" />
                </div>
                <div>
                  <div className="rm-overline mb-1">Headquarters</div>
                  <div className="font-display text-xl font-bold text-white">
                    {COMPANY.location}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 font-mono">
                    Working with clients across North America
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rm-card p-8 md:p-10 relative overflow-hidden"
            >
              <div className="rm-glow-bottom-left" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <div className="rm-overline mb-2">Contact form</div>
                    <h3 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight">
                      Start the conversation
                    </h3>
                  </div>
                  <div className="hidden md:block font-mono text-xs text-zinc-500">
                    Est. 2 min
                  </div>
                </div>

                {submitted ? (
                  <div
                    data-testid="contact-success"
                    className="border border-violet-500/40 bg-violet-500/5 p-8 text-center"
                  >
                    <div className="mx-auto h-12 w-12 flex items-center justify-center border border-violet-500 text-violet-300 mb-4">
                      <Check className="h-5 w-5" />
                    </div>
                    <h4 className="font-display text-2xl font-bold text-white mb-2">
                      Message received.
                    </h4>
                    <p className="text-zinc-400 max-w-md mx-auto">
                      Curtis will be in touch within one business day. If it's
                      urgent, you can call{" "}
                      <a
                        href={COMPANY.phoneHref}
                        className="text-violet-400 hover:underline"
                      >
                        {COMPANY.phoneDisplay}
                      </a>
                      .
                    </p>
                    <button
                      type="button"
                      onClick={() => setSubmitted(false)}
                      data-testid="contact-send-another"
                      className="rm-btn-secondary mt-6"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="rm-label" htmlFor="name">
                        Name *
                      </label>
                      <input
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        placeholder="Jane Doe"
                        className="rm-input"
                        data-testid="contact-input-name"
                        required
                      />
                    </div>
                    <div>
                      <label className="rm-label" htmlFor="email">
                        Email *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={onChange}
                        placeholder="jane@company.com"
                        className="rm-input"
                        data-testid="contact-input-email"
                        required
                      />
                    </div>
                    <div>
                      <label className="rm-label" htmlFor="phone">
                        Phone
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="Optional"
                        className="rm-input"
                        data-testid="contact-input-phone"
                      />
                    </div>
                    <div>
                      <label className="rm-label" htmlFor="company">
                        Company
                      </label>
                      <input
                        id="company"
                        name="company"
                        value={form.company}
                        onChange={onChange}
                        placeholder="Your business"
                        className="rm-input"
                        data-testid="contact-input-company"
                      />
                    </div>
                    <div>
                      <label className="rm-label" htmlFor="service">
                        Service of interest
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={form.service}
                        onChange={onChange}
                        className="rm-input"
                        data-testid="contact-input-service"
                      >
                        <option value="">Select a service</option>
                        {SERVICES.map((s) => (
                          <option key={s.id} value={s.title}>
                            {s.title}
                          </option>
                        ))}
                        <option value="Multi-channel">Multi-channel</option>
                      </select>
                    </div>
                    <div>
                      <label className="rm-label" htmlFor="budget">
                        Monthly ad budget
                      </label>
                      <select
                        id="budget"
                        name="budget"
                        value={form.budget}
                        onChange={onChange}
                        className="rm-input"
                        data-testid="contact-input-budget"
                      >
                        <option value="">Select a range</option>
                        {BUDGET_OPTIONS.map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="rm-label" htmlFor="message">
                        How can we help? *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        rows={5}
                        placeholder="Tell us about your business, where you're advertising now, and what growing your business would look like for you."
                        className="rm-input resize-none"
                        data-testid="contact-input-message"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="rm-label !mb-0">
                          Or send a voice message
                        </span>
                        <span className="text-[10px] font-mono text-zinc-500">
                          optional
                        </span>
                      </div>
                      <VoiceRecorder name={form.name} email={form.email} />
                    </div>
                    <div className="md:col-span-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-2">
                      <div className="text-xs font-mono text-zinc-500">
                        Pricing is custom — your first call is free.
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit"
                        className="rm-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                          </>
                        ) : (
                          <>
                            Send message <ArrowUpRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

      <Testimonials
        heading="What our clients say after working with us."
      />
    </div>
  );
}
